import { baseApi } from './baseApi'
import { Physician, PhysicianNote } from 'store/types'
import {
  CreatePhysicianNoteDto,
  UpdatePhysicianPreferredDmeOfficeDto,
  UpdatePhysicianSettingsDto,
  LabUpdatePhysicianAddressesDto,
  CreateLabPhysicianDto,
  UpdateLabPhysicianDto,
  PhysicianFollowUpPhysicianDto,
  AddFollowUpPhysicianDto,
  PatientStatusReportDto,
  UpdatePhysicianNoteDto,
} from 'ts-back-end/src/business/physician/dto/physician.dto'

type LatestPhysiciansParams = {
  officeId: number | string
  patientId: number | string
  count: number
}

type UpdateLabPhysicianParams = UpdateLabPhysicianDto

// Local types for the new "effective" response contracts until the backend exports them.
export type EffectiveFollowUpPhysiciansDto = {
  source: 'physician' | 'group' | 'office' | 'none'
  defaultFollowUpPhysicianId: number | null
  items: PhysicianFollowUpPhysicianDto[]
  noneExplicitlySet: boolean
}

export type EffectivePreferredDmeOfficeDto = {
  physicianPreferredDmeOfficeId: number | null
  groupPreferredDmeOfficeId: number | null
  officePreferredDmeOfficeId: number | null
  effectivePreferredDmeOfficeId: number | null
  source: 'physician' | 'group' | 'office' | null
  noneExplicitlySet: boolean
}

export const physicianApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLabPhysicians: builder.query<Physician[], { [key: string]: string }>({
      query: (query) => ({ url: `/lab/physicians/search`, params: query }),
    }),
    getLabPhysician: builder.query<Physician, { npi: string }>({
      query: (query) => ({ url: `/lab/physicians/${query.npi}` }),
    }),
    getLabPhysicianPatients: builder.query<Physician, { npi: string; page: number }>({
      query: ({ npi, ...query }) => ({
        url: `/lab/physicians/${npi}/patients`,
        params: query,
      }),
    }),
    getLabPhysicianNotes: builder.query<PhysicianNote[], { npi: string }>({
      query: ({ npi }) => ({ url: `/lab/physicians/${npi}/notes` }),
      providesTags: (result, error, { npi }) => [{ type: 'Notes', id: npi }],
    }),
    createLabPhysicianNote: builder.mutation<PhysicianNote, { npi: string; dto: CreatePhysicianNoteDto }>({
      query: ({ npi, dto }) => ({
        url: `/lab/physicians/${npi}/notes`,
        method: 'POST',
        body: dto,
      }),
      invalidatesTags: (result, error, { npi }) => [{ type: 'Notes', id: npi }],
    }),
    updateLabPhysicianNote: builder.mutation<
      PhysicianNote,
      { npi: string; uuid: string; dto: UpdatePhysicianNoteDto }
    >({
      query: ({ npi, uuid, dto }) => ({
        url: `/lab/physicians/${npi}/notes/${uuid}`,
        method: 'PATCH',
        body: dto,
      }),
      invalidatesTags: (result, error, { npi }) => [{ type: 'Notes', id: npi }],
    }),
    deleteLabPhysicianNote: builder.mutation<void, { npi: string; uuid: string }>({
      query: ({ npi, uuid }) => ({
        url: `/lab/physicians/${npi}/notes/${uuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { npi }) => [{ type: 'Notes', id: npi }],
    }),
    updateLabPhysicianSettings: builder.mutation<
      Physician,
      { npi: string; settings: Partial<UpdatePhysicianSettingsDto> }
    >({
      query: (query) => ({
        url: `/lab/physicians/${query.npi}/settings`,
        method: 'PUT',
        body: query.settings,
      }),
    }),
    updateLabPhysicianPreferredDmeOffice: builder.mutation<
      { preferredDmeOfficeId: number | null; affectedPhysicians: number },
      { npi: string; preferredDmeOfficeId: number | null; isNone?: boolean }
    >({
      query: ({ npi, preferredDmeOfficeId, isNone }) => ({
        url: `/lab/physicians/${npi}/preferred-dme-office`,
        method: 'PUT',
        body: { preferredDmeOfficeId, isNone } as UpdatePhysicianPreferredDmeOfficeDto,
      }),
      invalidatesTags: (result, error, { npi }) => [{ type: 'PhysicianSettings', id: npi }],
    }),
    updateLabPhysicianAddresses: builder.mutation<
      Physician,
      { npi: string; addresses: LabUpdatePhysicianAddressesDto }
    >({
      query: (query) => ({
        url: `/lab/physicians/${query.npi}/addresses`,
        method: 'PUT',
        body: query.addresses,
      }),
    }),
    deleteLabPhysician: builder.mutation<
      Physician,
      { physicianId: number; addressUuid: string; replaceAddressUuid: string }
    >({
      query: ({ physicianId, addressUuid, replaceAddressUuid }) => ({
        url: `/lab/physicians/${physicianId}/addresses/${addressUuid}`,
        method: 'DELETE',
        body: { replaceAddressUuid },
      }),
    }),
    getDmePhysicians: builder.query<Physician[], { [key: string]: string }>({
      query: (query) => ({ url: `/lab/physicians`, params: query }),
    }),
    getLabPhysicianListing: builder.query<Physician[], { [key: string]: string }>({
      query: (query) => ({ url: `/lab/physicians/list`, params: query }),
    }),
    getDmeLatestPhysicians: builder.query<Physician[], LatestPhysiciansParams>({
      query: (query) => ({ url: `/lab/physicians/latest`, params: query }),
    }),
    getPhysicianHistory: builder.query<any, { id: number }>({
      query: (query) => ({ url: `/lab/physicians/${query.id}/history` }),
    }),
    approveAddress: builder.mutation<Physician, { orderId: string; id: string; uuid: string }>({
      query: ({ orderId, id, uuid }) => ({
        method: `PUT`,
        url: `/lab/physicians/${id}/addresses/${uuid}/approval`,
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
    }),
    rejectAddress: builder.mutation<Physician, { orderId: string; id: string; uuid: string }>({
      query: ({ orderId, id, uuid }) => ({
        method: `PUT`,
        url: `/lab/physicians/${id}/addresses/${uuid}/rejection`,
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
    }),
    createLabPhysician: builder.mutation<Physician, CreateLabPhysicianDto>({
      query: (physician) => ({
        method: 'POST',
        url: `/lab/physicians`,
        body: physician,
      }),
    }),
    updateLabPhysician: builder.mutation<Physician, UpdateLabPhysicianParams>({
      query: ({ npi, ...body }) => ({
        method: 'PATCH',
        url: `/lab/physicians/${npi}`,
        body,
      }),
    }),
    generatePatientStatusReport: builder.mutation<
      void,
      {
        npi: string
        dto: PatientStatusReportDto
      }
    >({
      query: ({ npi, dto }) => ({
        method: 'POST',
        url: `/lab/physicians/${npi}/generate-patient-status-report`,
        body: dto,
      }),
      invalidatesTags: (result, error, { npi }) => [{ type: 'Physician', id: npi }],
    }),
    physiciansLookup: builder.query<Physician[], { [key: string]: string }>({
      query: (query) => ({ url: `/lab/physicians/lookup`, params: query }),
    }),
    getFollowUpPhysicians: builder.query<Physician[], {}>({
      query: () => ({
        url: `/lab/physicians/follow-up-physicians`,
      }),
    }),
    getPhysicianFollowUpPhysicians: builder.query<
      PhysicianFollowUpPhysicianDto[],
      { physicianNpi: string }
    >({
      query: ({ physicianNpi }) => ({
        url: `/lab/physicians/${physicianNpi}/follow-up-physicians`,
      }),
      providesTags: (result, error, { physicianNpi }) => [
        { type: 'FollowUpProviders', id: physicianNpi },
      ],
    }),
    addPhysicianFollowUpPhysician: builder.mutation<void, AddFollowUpPhysicianDto>({
      query: ({ physicianNpi, followUpPhysicianNpi }) => ({
        method: 'POST',
        url: `/lab/physicians/${physicianNpi}/follow-up-physicians`,
        body: { followUpPhysicianNpi },
      }),
      invalidatesTags: (result, error, { physicianNpi }) => [
        { type: 'FollowUpProviders', id: physicianNpi },
      ],
    }),
    removePhysicianFollowUpPhysician: builder.mutation<
      void,
      { physicianNpi: string; followUpPhysicianNpi: string }
    >({
      query: ({ physicianNpi, followUpPhysicianNpi }) => ({
        method: 'DELETE',
        url: `/lab/physicians/${physicianNpi}/follow-up-physicians/${followUpPhysicianNpi}`,
      }),
      invalidatesTags: (result, error, { physicianNpi }) => [
        { type: 'FollowUpProviders', id: physicianNpi },
      ],
    }),
    setDefaultFollowUpPhysician: builder.mutation<
      void,
      { physicianNpi: string; defaultFollowUpPhysicianNpi: string | null }
    >({
      query: ({ physicianNpi, defaultFollowUpPhysicianNpi }) => ({
        method: 'PUT',
        url: `/lab/physicians/${physicianNpi}/default-follow-up-physician`,
        body: { defaultFollowUpPhysicianNpi },
      }),
      invalidatesTags: (result, error, { physicianNpi }) => [
        { type: 'FollowUpProviders', id: physicianNpi },
      ],
    }),
    updatePhysicianFollowUpCareNone: builder.mutation<void, { physicianNpi: string; isNone: boolean }>({
      query: ({ physicianNpi, isNone }) => ({
        url: `/lab/physicians/${physicianNpi}/follow-up-care-none`,
        method: 'PUT',
        body: { isNone },
      }),
      invalidatesTags: (result, error, { physicianNpi }) => [
        { type: 'FollowUpProviders', id: physicianNpi },
      ],
    }),
    getEffectiveFollowUpPhysicians: builder.query<
      EffectiveFollowUpPhysiciansDto,
      { physicianNpi: string; officeId?: number | null }
    >({
      query: ({ physicianNpi, officeId }) => ({
        url: `/lab/physicians/${physicianNpi}/follow-up-physicians/effective`,
        params: officeId != null ? { officeId } : undefined,
      }),
      providesTags: (result, error, { physicianNpi }) => [
        { type: 'FollowUpProviders', id: physicianNpi },
      ],
    }),
    getEffectivePreferredDmeOffice: builder.query<
      EffectivePreferredDmeOfficeDto,
      { physicianNpi: string; officeId?: number | null }
    >({
      query: ({ physicianNpi, officeId }) => ({
        url: `/lab/physicians/${physicianNpi}/preferred-dme-office/effective`,
        params: officeId != null ? { officeId } : undefined,
      }),
      providesTags: (result, error, { physicianNpi }) => [{ type: 'PhysicianSettings', id: physicianNpi }],
    }),
  }),
})

export const {
  useGetLabPhysiciansQuery,
  useLazyGetLabPhysiciansQuery,
  useGetPhysicianHistoryQuery,
  useGetDmePhysiciansQuery,
  usePhysiciansLookupQuery,
  useGetDmeLatestPhysiciansQuery,
  useApproveAddressMutation,
  useRejectAddressMutation,
  useGetLabPhysicianListingQuery,
  useGetLabPhysicianQuery,
  useGetLabPhysicianNotesQuery,
  useGetLabPhysicianPatientsQuery,
  useCreateLabPhysicianNoteMutation,
  useUpdateLabPhysicianNoteMutation,
  useDeleteLabPhysicianNoteMutation,
  useUpdateLabPhysicianSettingsMutation,
  useUpdateLabPhysicianPreferredDmeOfficeMutation,
  useUpdateLabPhysicianAddressesMutation,
  useDeleteLabPhysicianMutation,
  useCreateLabPhysicianMutation,
  useUpdateLabPhysicianMutation,
  useGetFollowUpPhysiciansQuery,
  useGetPhysicianFollowUpPhysiciansQuery,
  useLazyGetPhysicianFollowUpPhysiciansQuery,
  useAddPhysicianFollowUpPhysicianMutation,
  useRemovePhysicianFollowUpPhysicianMutation,
  useSetDefaultFollowUpPhysicianMutation,
  useUpdatePhysicianFollowUpCareNoneMutation,
  useGeneratePatientStatusReportMutation,
  useGetEffectiveFollowUpPhysiciansQuery,
  useLazyGetEffectiveFollowUpPhysiciansQuery,
  useGetEffectivePreferredDmeOfficeQuery,
} = physicianApi
